import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Heart, Shield, MessageSquare, Phone, PenLine, Zap } from 'lucide-react';
import { SparkleGuide } from './guide/SparkleGuide';
import { SafetyPlanBuilder } from './SafetyPlanBuilder';
import { CrisisButton } from './CrisisButton';
import { MemoryBox } from './MemoryBox';
import { CopingToolsLibrary } from './CopingToolsLibrary';
import { QuickMessageTemplates } from './QuickMessageTemplates';
import { CrisisDisclaimer } from '@/legal/components/CrisisDisclaimer';
import { LifelineLetters } from './LifelineLetters';
import { useDisclaimerConsent } from '@/hooks/useDisclaimerConsent';
import { useNavigate } from 'react-router-dom';

interface CrisisSuiteProps {
  user: any;
}

export const CrisisSuite = ({ user }: CrisisSuiteProps) => {
  const navigate = useNavigate();
  const [activeComponent, setActiveComponent] = useState<string | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const { hasCrisisConsent, acceptCrisisDisclaimer, loading } = useDisclaimerConsent(user?.id);
  const [showLifelineLetters, setShowLifelineLetters] = useState(false);

  const handleEmergency = () => {
    if (!hasCrisisConsent) {
      setShowDisclaimer(true);
      return;
    }
    setActiveComponent('crisis-button');
  };

  const handleComponentAccess = (component: string) => {
    if (!hasCrisisConsent) {
      setShowDisclaimer(true);
      return;
    }
    setActiveComponent(component);
  };

  const handleAcceptDisclaimer = async () => {
    const success = await acceptCrisisDisclaimer();
    if (success) {
      setShowDisclaimer(false);
    }
  };

  const handleCancelDisclaimer = () => {
    setShowDisclaimer(false);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading crisis suite...</p>
        </div>
      </div>
    );
  }

  if (showLifelineLetters) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <LifelineLetters user={user} onBack={() => setShowLifelineLetters(false)} />
      </div>
    );
  }

  if (activeComponent === 'safety-plan') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Button 
          onClick={() => setActiveComponent(null)}
          variant="outline" 
          className="mb-4"
        >
          ← Back to Crisis Suite
        </Button>
        <SafetyPlanBuilder user={user} />
      </div>
    );
  }

  if (activeComponent === 'crisis-button') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Button 
          onClick={() => setActiveComponent(null)}
          variant="outline" 
          className="mb-4"
        >
          ← Back to Crisis Suite
        </Button>
        <CrisisButton user={user} />
      </div>
    );
  }

  if (activeComponent === 'memory-box') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Button 
          onClick={() => setActiveComponent(null)}
          variant="outline" 
          className="mb-4"
        >
          ← Back to Crisis Suite
        </Button>
        <MemoryBox user={user} />
      </div>
    );
  }

  if (activeComponent === 'coping-tools') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Button 
          onClick={() => setActiveComponent(null)}
          variant="outline" 
          className="mb-4"
        >
          ← Back to Crisis Suite
        </Button>
        <CopingToolsLibrary user={user} />
      </div>
    );
  }

  if (activeComponent === 'quick-messages') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Button 
          onClick={() => setActiveComponent(null)}
          variant="outline" 
          className="mb-4"
        >
          ← Back to Crisis Suite
        </Button>
        <QuickMessageTemplates user={user} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Mandatory Crisis Disclaimer */}
      <CrisisDisclaimer 
        isOpen={showDisclaimer}
        onAccept={handleAcceptDisclaimer}
        onCancel={handleCancelDisclaimer}
      />
      
      <Card className="border-primary/20 bg-gradient-therapeutic">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2">
            <CardTitle className="text-2xl text-primary flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Crisis & Safety Suite
            </CardTitle>
            <SparkleGuide
              title="How to use the Crisis Suite"
              items={[
                { title: 'Safety plan', description: 'Create a personalised plan for difficult moments - your go-to resource when struggling.' },
                { title: 'Memory box', description: 'Store photos, letters, and reminders of reasons to live for grounding.' },
                { title: 'Lifeline letters', description: 'Write letters to your future self or receive messages of hope.' },
                { title: 'In-the-moment support', description: 'Access quick breathing, grounding, and coping tools when you need them.' }
              ]}
            />
          </div>
          <p className="text-foreground mt-2">You're not alone. Let's make sure you're safe now.</p>
          <div className="mt-4 max-w-2xl mx-auto">
            <p className="text-foreground mb-3">This suite provides support for anyone experiencing crisis, including:</p>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-left text-foreground/90">
              <div className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                <span>Suicidal thoughts or planning</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                <span>Self-harm urges</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                <span>Emotional overwhelm (BPD)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                <span>PTSD flashbacks</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                <span>OCD compulsions</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                <span>Severe distress</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-center text-primary font-medium mb-6">
            If you're in immediate danger, call emergency services.
          </p>

          {/* In-the-Moment Toolkit Highlight */}
          <Card className="mb-6 border-secondary/50 bg-secondary/10">
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-center sm:text-left">
                  <div className="p-3 rounded-full bg-secondary/20">
                    <Zap className="h-6 w-6 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">In-the-Moment Toolkit</p>
                    <p className="text-sm text-muted-foreground">
                      Quick support tools: breathing, grounding, EFT, meditation & more
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/sanctuary')} 
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                >
                  Open Toolkit
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Main Action Buttons */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <Button
              onClick={handleEmergency}
              size="lg"
              className="h-20 flex-col gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Phone className="h-6 w-6" />
              I need to talk now
            </Button>
            
            <Button
              onClick={() => handleComponentAccess('safety-plan')}
              size="lg"
              variant="outline"
              className="h-20 flex-col gap-2 border-secondary hover:bg-secondary/10"
            >
              <Shield className="h-6 w-6" />
              Open my safety plan
            </Button>
            
            <Button
              onClick={() => handleComponentAccess('memory-box')}
              size="lg"
              variant="outline"
              className="h-20 flex-col gap-2 border-soft-accent hover:bg-soft-accent/10"
            >
              <Heart className="h-6 w-6" />
              Memory box & grounding
            </Button>
          </div>

          {/* Additional Tools */}
          <div className="grid md:grid-cols-3 gap-4">
            <Button
              onClick={() => {
                if (!hasCrisisConsent) {
                  setShowDisclaimer(true);
                  return;
                }
                setShowLifelineLetters(true);
              }}
              size="lg"
              variant="outline"
              className="h-20 flex-col gap-2 border-secondary hover:bg-secondary/10"
            >
              <PenLine className="h-6 w-6 text-primary" />
              Lifeline Letters
            </Button>
            
            <Button
              onClick={() => handleComponentAccess('coping-tools')}
              size="lg"
              variant="outline"
              className="h-20 flex-col gap-2 border-secondary hover:bg-secondary/10"
            >
              <Heart className="h-6 w-6 text-primary" />
              Coping Tools
            </Button>
            
            <Button
              onClick={() => handleComponentAccess('quick-messages')}
              size="lg"
              variant="outline"
              className="h-20 flex-col gap-2 border-secondary hover:bg-secondary/10"
            >
              <MessageSquare className="h-6 w-6 text-primary" />
              Quick Messages
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};